import { useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { useTemplateDatabase } from 'src/redux/services/ubem'
import { getProject } from 'src/redux/slices/project/endpoints'
import {
  mutationFixedCacheKeys,
  uploadUserTemplateLibUmibuilder,
} from 'src/redux/slices/umibuilder/endpoints'
import { changeArchetypeField } from 'src/redux/slices/umibuilder/reducers'
import {
  makeSelectArchetypeSubgroup,
  selectUmiBuilderFromCloud,
} from 'src/redux/slices/umibuilder/selectors'
import { TemplateMetadata } from 'src/redux/types'

import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Container from '@material-ui/core/Container'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import LinearProgress from '@material-ui/core/LinearProgress'
import CancelIcon from '@material-ui/icons/Cancel'
import { GridRowData, GridRowId, GridRowParams } from '@mui/x-data-grid'

import TemplateTable from './TemplateTable'

type AssignTemplateDialogProps = {
  archetypeID: string | null | undefined
  primaryField: string | null | undefined
  open: boolean
  setOpen(arg: boolean): void
}

interface Row extends Partial<Omit<TemplateMetadata, 'id'>>, GridRowData {}

export type TemplateRow = {
  row: Row
  id: GridRowId
}

export type AssignTemplateRowCb = {
  ({ row, id }: TemplateRow): void
}

export type TemplateTableProps = {
  assign: AssignTemplateRowCb
}

const AssignTemplateDialog: React.FC<AssignTemplateDialogProps> = ({
  archetypeID,
  primaryField,
  open,
  setOpen,
}) => {
  const dispatch = useDispatch()
  const fromCloud = useSelector(selectUmiBuilderFromCloud)
  const { data: project } = getProject.useQuery('')
  const { isLoading: isLoadingDatabaseTemplates } = useTemplateDatabase.useQuery('')
  const [triggerUploadUserTemplateLib, { isLoading: isLoadingUserTemplates }] =
    uploadUserTemplateLibUmibuilder.useMutation({
      fixedCacheKey: mutationFixedCacheKeys.uploadUserTemplateLib,
    })
  // TODO: Figure out why this keeps refetching
  useEffect(() => {
    if (fromCloud && project?.template_lib_id) {
      const body = { fromCloud, id: project.template_lib_id }
      triggerUploadUserTemplateLib({ body })
    }
  }, [project?.template_lib_id, fromCloud, triggerUploadUserTemplateLib])
  const currentTemplate = useSelector(
    makeSelectArchetypeSubgroup({ field: primaryField, archetypeID }),
  )?.template

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const assign: AssignTemplateRowCb = useCallback(
    ({ row }) => {
      if (!archetypeID || !primaryField) return
      dispatch(
        changeArchetypeField({
          field: primaryField,
          archetypeID,
          key: 'template',
          value: row.Name,
        }),
      )
      dispatch(
        changeArchetypeField({
          field: primaryField,
          archetypeID,
          key: 'isUser',
          value: row.Type?.includes('User'),
        }),
      )
      dispatch(
        changeArchetypeField({
          field: primaryField,
          archetypeID,
          key: 'cloudLibraryId',
          value: row.cloudLibraryId,
        }),
      )
      toast.success('Assigned template!')
      setOpen(false)
    },
    [primaryField, archetypeID, dispatch, setOpen],
  )

  const unassign = useCallback(() => {
    if (!archetypeID || !primaryField) return
    dispatch(
      changeArchetypeField({ field: primaryField, archetypeID, key: 'template', value: null }),
    )
    dispatch(
      changeArchetypeField({ field: primaryField, archetypeID, key: 'isUser', value: undefined }),
    )
    toast.success('Unassigned template!')
  }, [primaryField, archetypeID, dispatch])

  return (
    <Dialog fullScreen open={open} onClose={handleClose}>
      <Container>
        <DialogTitle id='template-assignment-dialog'>Select Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a template to assign to the current archetype ({archetypeID}). Filter by Climate
            Zone or building typology.
          </DialogContentText>
          {currentTemplate && <Chip onDelete={unassign} label={currentTemplate} />}
          {(isLoadingDatabaseTemplates || isLoadingUserTemplates) && <LinearProgress />}
          <TemplateTable assign={assign} />
        </DialogContent>
      </Container>
      <DialogActions>
        <Button variant='contained' onClick={handleClose} color='primary' endIcon={<CancelIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AssignTemplateDialog
